import { useEffect, useMemo, useState } from 'react'
import { statusService } from '../services/statusService'
import { employeeService } from '../services/employeeService'
import queueService from '../services/queueService'

/**
 * Petición compartida: varios componentes montados a la vez (página, tablero y
 * tabla) reutilizan la misma carga. Si falla, se descarta para reintentar.
 * @type {Promise<[Object, Object, Object]>|null}
 */
let catalogsRequest = null

const loadCatalogs = () => {
  if (!catalogsRequest) {
    catalogsRequest = Promise.all([
      statusService.getAll(),
      employeeService.getAll(),
      queueService.getAll(),
    ]).then((results) => {
      if (results.some((r) => !r.success)) catalogsRequest = null
      return results
    })
  }
  return catalogsRequest
}

/**
 * Descarta la caché de catálogos (p. ej. tras editar colas en Configuración):
 * los componentes que se monten después verán los datos nuevos.
 */
export const invalidateTicketCatalogs = () => {
  catalogsRequest = null
}

/**
 * Catálogos de apoyo para mostrar y filtrar tickets: estados (`Tickets.status`),
 * empleados (`public.Empleados`, para responsable y autor) y colas de atención
 * (`Tickets.queue`). Se cargan una vez por sesión y se exponen también indexados.
 *
 * @returns {{
 *   statuses: Array<Object>,
 *   employees: Array<Object>,
 *   queues: Array<Object>,
 *   statusById: Record<string, Object>,
 *   employeeById: Record<string, Object>,
 *   employeeByAuthId: Record<string, Object>,
 *   queueById: Record<string, Object>,
 *   loading: boolean
 * }}
 */
export default function useTicketCatalogs() {
  const [statuses, setStatuses] = useState([])
  const [employees, setEmployees] = useState([])
  const [queues, setQueues] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true

    loadCatalogs().then(([statusRes, employeeRes, queueRes]) => {
      if (!active) return
      // Un catálogo que falla no bloquea la vista: se muestran los ids.
      if (statusRes.success)
        setStatuses([...statusRes.data].sort((a, b) => a.id - b.id))
      if (employeeRes.success) setEmployees(employeeRes.data)
      if (queueRes.success) setQueues(queueRes.data)
      setLoading(false)
    })

    return () => {
      active = false
    }
  }, [])

  const statusById = useMemo(
    () => Object.fromEntries(statuses.map((s) => [s.id, s])),
    [statuses]
  )
  const employeeById = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.id, e])),
    [employees]
  )
  // El autor de un ticket (`userId`) es el auth id, que en Empleados es `id_user`.
  const employeeByAuthId = useMemo(
    () =>
      Object.fromEntries(
        employees.filter((e) => e.id_user).map((e) => [e.id_user, e])
      ),
    [employees]
  )
  const queueById = useMemo(
    () => Object.fromEntries(queues.map((q) => [q.id, q])),
    [queues]
  )

  return {
    statuses,
    employees,
    queues,
    statusById,
    employeeById,
    employeeByAuthId,
    queueById,
    loading,
  }
}
